import React, { useState } from "react";

import CheckIcon from "theme/components/icons/CheckIcon";
import { GetProductByPartNo } from "core-web/libs/GrebbCommerceAPI/Products";
import Loading from "theme/components/Loading";
import PlusIcon from "theme/components/icons/PlusIcon";
import PropTypes from "prop-types";
import TextStyles from "theme/components/text/TextStyles";
import ThemeButton from "theme/components/buttons/ThemeButton";
import addPackageToBasket from "core-web/state/models/Basket/dispatches/addPackageToBasket";
import addToBasket from "core-web/state/models/Basket/dispatches/addToBasket";
import getBasketId from "core-web/state/models/Basket/utils/getBasketId";
import { getState } from "core-web/state";
import hidePopup from "core-web/state/models/Popups/dispatches/hidePopup";
import resolveSequentially from "core-web/utils/resolveSequentially";
import showPopup from "core-web/state/models/Popups/dispatches/showPopup";
import { useTranslation } from "react-i18next";

const AddToCart = ({
    //inStock, (not using inStock/onHand because products should always be available on demand)
    quantity = 1,
    productId = null,
    selectedProductVariants = {},
    isPackage = false,
    isQuantityPackage = false,
    mainProduct = {},
    ...props
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [productAdded, setProductAdded] = useState(false);

    const addToCart = async (quantity) => {
        setIsLoading(true);
        const promises = [];

        await getBasketId();

        let includedProducts = Object.values(selectedProductVariants);

        if (isPackage) {
            const mainItem = {
                part_no: mainProduct.part_no || mainProduct.partNo,
                quantity: (mainProduct.quantity || 1) * quantity,
                price_list_id: mainProduct.price_list_id,
                isQuantityPackage,
            };
            const items = [mainItem];

            if (isQuantityPackage) {
                const packageProduct = await GetProductByPartNo(mainProduct.part_no || mainProduct.partNo);
                includedProducts = packageProduct.data.included_products;
            }

            const subItems = includedProducts.map((product) => {
                return {
                    part_no: product.part_no || product.partNo,
                    quantity: (product.quantity || 1) * quantity,
                    price_list_id: product.price_list_id,
                };
            });
            items.push(...subItems);

            promises.push(addPackageToBasket.bind(null, items, { ...mainProduct, quantity }));
        } else {
            // Add the main product
            const googleDiscountPrice = selectedProductVariants[productId]?.googleDiscountPrice;
            const specialPrice = selectedProductVariants[productId]?.specialPrice;
            const infos = {};

            if (googleDiscountPrice) {
                const roundedGoogleDiscountPrice = parseFloat(googleDiscountPrice.toFixed(4), 10);
                infos["google_discount_price"] = roundedGoogleDiscountPrice;
            }

            if (specialPrice) {
                const roundedSpecialPrice = parseFloat(specialPrice.toFixed(4), 10);
                infos["special_price"] = roundedSpecialPrice;
            }

            promises.push(
                addToBasket.bind(
                    null,
                    selectedProductVariants[productId].part_no,
                    quantity,
                    selectedProductVariants[productId].price_list_id,
                    infos,
                    false,
                    specialPrice || googleDiscountPrice || undefined,
                ),
            );
        }

        if (!promises?.length) {
            setIsLoading(false);
        }

        resolveSequentially(promises)
            .then(() => {
                setIsLoading(false);
                const pageType = getState("page").type;
                setProductAdded(true);
                if (pageType !== "storm_checkout") {
                    showPopup("addedToBasketPopup", {
                        [mainProduct.id]: {
                            image: mainProduct.format.image,
                            name: mainProduct.name,
                            productId: mainProduct.id,
                        },
                    });
                }
                // We only display the feedback for 3500ms
                setTimeout(() => {
                    setProductAdded(false);
                }, 3500);

                if (pageType !== "storm_checkout") {
                    setTimeout(() => {
                        hidePopup("addedToBasketPopup", mainProduct.id);
                    }, 5500);
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error(error);
            });
    };

    const Icon = productAdded ? CheckIcon : PlusIcon;

    return (
        <ThemeButton
            buttonTheme="blue_solid"
            height="52px"
            width="100%"
            onClick={() => {
                if (!isLoading) {
                    addToCart(quantity);
                }
            }}
            {...props}
        >
            {isLoading ? (
                <Loading mx="auto" justifySelf="center" width="20px" height="20px" />
            ) : (
                <>
                    <TextStyles as="p" textStyles="Myriad Pro/18-100-semibold-semiextended">
                        {productAdded ? t("pdp.added") : t("pdp.add_to_cart")}
                    </TextStyles>
                    <Icon height="12px" width="12px" aria-hidden />
                </>
            )}
        </ThemeButton>
    );
};

AddToCart.propTypes = {
    // inStock: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    isPackage: PropTypes.bool,
    isQuantityPackage: PropTypes.bool,
    isStoreProduct: PropTypes.bool,
    mainProduct: PropTypes.object,
    productId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    quantity: PropTypes.number,
    selectedProductVariants: PropTypes.object,
};

export default AddToCart;
